import styled from '@emotion/styled';
import { CncReport } from '@gameonly/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Button, { WhiteButton } from '../ui/Button';
import LoadingButton from '../ui/LoadingButton';
import useReport from './context/useReport';

const StyledDialogContent = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ValidationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
`;

const ReportDetails = styled.div<{ type: string }>`
  background-color: var(--color-accent);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ type }) => (type === 'modal' ? '16px' : '0')};
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

const ValidateReportModal = ({ open, onClose }: Props) => {
  const [isValidated, setIsValidated] = useState(false);

  const { reportId } = useParams();
  const {
    report,
    validateReport,
    loadingValidateReport: loading,
  } = useReport(reportId);
  const { t } = useTranslation(['cnc-reports']);

  if (!report) return null;

  const handleValidate = async () => {
    if (!isValidated) {
      await validateReport();
      setIsValidated(true);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('cnc-reports:validation-modal.title')}</DialogTitle>
      <StyledDialogContent dividers>
        {isValidated ? (
          <ValidationInformation report={report} type="modal" />
        ) : (
          <DialogContentText>
            {t('cnc-reports:validation-modal.explanation-text')}
          </DialogContentText>
        )}
      </StyledDialogContent>
      <DialogActions>
        {isValidated ? (
          <Button onClick={onClose}>{t('common:ok')}</Button>
        ) : (
          <>
            <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
            <LoadingButton onClick={handleValidate} loading={loading}>
              {t('common:validate')}
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const ValidationInformation = ({
  report,
  type,
}: {
  report: CncReport;
  type: string;
}) => {
  const { t } = useTranslation(['cnc-reports']);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const CNCid = report.CNCid;
  const reportName = report.name;

  const handleCopyToClipboard = () => {
    if (CNCid) {
      navigator.clipboard.writeText(CNCid);
      setCopyFeedback(true);
    }
  };

  const handleCloseFeedback = () => setCopyFeedback(false);

  return (
    <>
      <Snackbar
        open={copyFeedback}
        autoHideDuration={3000}
        onClose={handleCloseFeedback}
        message={t('cnc-reports:validation-modal.copied-feedback')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: '200', position: 'fixed' }}
      />
      <ValidationHeader>
        <CheckCircleIcon
          fontSize="large"
          color={type === 'modal' ? 'primary' : 'inherit'}
        />
        <p className="h3b">
          {t('cnc-reports:validation-modal.validated-report')}
        </p>
      </ValidationHeader>
      <p className="h6r">{t('cnc-reports:validation-modal.validation-text')}</p>
      <ReportDetails type={type}>
        <p className="h6r">
          <Trans
            i18nKey="cnc-reports:validation-modal.CNC-id-text"
            values={{ CNCid, reportName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <Tooltip title={t('cnc-reports:validation-modal.copy-tooltip')}>
          <IconButton
            onClick={handleCopyToClipboard}
            aria-label="copy"
            size="small"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ReportDetails>
    </>
  );
};

export default ValidateReportModal;
